import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Share2,
  Copy,
  Check,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { aiAssistantService } from "@/services/aiAssistantService";

interface ShareOutputDialogProps {
  outputId: string;
  children?: React.ReactNode;
}

export const ShareOutputDialog: React.FC<ShareOutputDialogProps> = ({
  outputId,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [isPublic, setIsPublic] = useState(false);
  const [publicId, setPublicId] = useState<string | undefined>();
  const [copiedLink, setCopiedLink] = useState(false);
  const { toast } = useToast();

  // Check if output is already public when dialog opens
  useEffect(() => {
    const checkPublicStatus = async () => {
      if (!isOpen) return;

      setIsCheckingStatus(true);
      try {
        const { data, error } =
          await aiAssistantService.isOutputPublic(outputId);
        if (error) {
          console.error("Error checking public status:", error);
        } else if (data) {
          setIsPublic(data.isPublic);
          setPublicId(data.publicId);
        }
      } catch (error) {
        console.error("Failed to check public status:", error);
      } finally {
        setIsCheckingStatus(false);
      }
    };

    checkPublicStatus();
  }, [isOpen, outputId]);

  const handleTogglePublic = async (newPublicState: boolean) => {
    setIsLoading(true);
    try {
      if (newPublicState) {
        // Make public
        const { data, error } =
          await aiAssistantService.makeOutputPublic(outputId);
        if (error) {
          throw error;
        }
        if (data) {
          setPublicId(data.publicId);
          setIsPublic(true);
          toast({
            title: "Output is now public",
            description: "Anyone with the link can view this output",
          });
        }
      } else {
        // Make private
        const { error } = await aiAssistantService.makeOutputPrivate(outputId);
        if (error) {
          throw error;
        }
        setPublicId(undefined);
        setIsPublic(false);
        toast({
          title: "Output is now private",
          description: "The public link has been disabled",
        });
      }
    } catch (error) {
      console.error("Error toggling public status:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to update sharing status",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getPublicUrl = () => {
    if (!publicId) return "";
    return `${window.location.origin}/public/output/${publicId}`;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
      toast({
        title: "Copied to clipboard",
        description: "Link has been copied",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to copy",
        description: "Please try copying manually",
      });
    }
  };

  const openInNewTab = () => {
    if (publicId) {
      window.open(getPublicUrl(), "_blank");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <Share2 className="h-4 w-4" />
            Share
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share Assistant Output
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Public Access Toggle */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="text-sm font-medium">Public Access</Label>
                <p className="text-xs text-gray-500">
                  Allow anyone with the link to view this output
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isCheckingStatus ? (
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                ) : (
                  <>
                    {isPublic ? (
                      <Eye className="h-4 w-4 text-green-600" />
                    ) : (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    )}
                    <Switch
                      checked={isPublic}
                      onCheckedChange={handleTogglePublic}
                      disabled={isLoading || isCheckingStatus}
                    />
                  </>
                )}
              </div>
            </div>

            {isPublic && (
              <Badge variant="secondary" className="text-green-700 bg-green-50">
                Public - Anyone with the link can view
              </Badge>
            )}
          </div>

          {/* Public Link Section */}
          {isPublic && publicId && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Public Link
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={getPublicUrl()}
                    readOnly
                    className="font-mono text-xs"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(getPublicUrl())}
                    className="shrink-0"
                  >
                    {copiedLink ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <Button
                variant="outline"
                onClick={openInNewTab}
                className="w-full flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Open in New Tab
              </Button>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong>How sharing works:</strong> When enabled, anyone with the
              public link can view this specific output. The link will remain
              active until you disable public access.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
